import React, { useEffect, useRef, useState } from 'react';
import { arrayOf, bool, func, object, oneOf, string, number } from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { addToCart } from '../../ducks/cart.duck';

import css from './AddToCartButton.module.css';

/**
 * AddToCartButton
 *
 * A small, self-contained connected button that adds a listing to the basket.
 * It is used both on listing cards (where the whole card is a link, so the
 * click must not bubble up and navigate) and on the listing page.
 *
 * A successful add also opens the mini-basket drawer (see MiniCart), which is
 * where the shopper goes on to the basket or checkout.
 *
 * @component
 * @param {Object} props
 * @param {Object} props.listing listing entity to add
 * @param {number} [props.quantity=1] quantity to add
 * @param {Array} [props.variants] the chosen product variants (size, volume, …),
 *   as returned by util/productVariants.js getVariantSelectionEntries
 * @param {boolean} [props.disabled] e.g. while a variant is still unchosen
 * @param {('incomplete'|'outOfStock')} [props.disabledReason] why, so the
 *   hint says "sold out" rather than "choose your options" when it is the
 *   chosen combination that has run out
 * @param {Function} [props.onChooseOptions] when given, a button that is only
 *   disabled because options are unchosen becomes a "Choose options" button
 *   calling this instead (used in the phone buy bar, where the options are in
 *   a pop-up)
 * @param {boolean} [props.opensProductPage] on a listing card for a product sold
 *   in several sizes or colours: the click is let through to the surrounding
 *   card link, so the shopper lands on the product page to choose one, rather
 *   than an unspecified version going into the basket
 * @param {boolean} [props.showDisabledReason] show why the button is disabled
 *   as visible text under it, not only as a tooltip
 * @param {number} [props.maxQuantity] how many of this exact version the brand
 *   has, so the basket can't be stacked past it
 * @param {('card'|'page')} [props.variant='card'] visual variant
 * @param {Function} props.onAddToCart injected thunk
 */
export const AddToCartButtonComponent = props => {
  const {
    className,
    rootClassName,
    listing,
    quantity = 1,
    variants = [],
    disabled = false,
    disabledReason = 'incomplete',
    onChooseOptions,
    opensProductPage = false,
    showDisabledReason = false,
    maxQuantity,
    variant = 'card',
    onAddToCart,
  } = props;

  const intl = useIntl();
  const [added, setAdded] = useState(false);
  const resetTimeout = useRef(null);

  useEffect(() => () => window.clearTimeout(resetTimeout.current), []);

  const isChooseOptions = disabled && disabledReason === 'incomplete' && !!onChooseOptions;

  const handleClick = e => {
    if (opensProductPage) {
      // Let the surrounding card link navigate to the product page.
      return;
    }
    // Prevent the surrounding card link from navigating.
    e.preventDefault();
    e.stopPropagation();
    if (isChooseOptions) {
      onChooseOptions();
      return;
    }
    const wasAdded = onAddToCart(listing, quantity, variants, maxQuantity);
    // Only confirm when something actually went into the basket.
    if (wasAdded) {
      setAdded(true);
      window.clearTimeout(resetTimeout.current);
      resetTimeout.current = window.setTimeout(() => setAdded(false), 2000);
    }
  };

  const isDisabled = disabled && !isChooseOptions;
  const classes = classNames(rootClassName || css.root, className, {
    [css.page]: variant === 'page',
    [css.added]: added,
    [css.disabled]: isDisabled,
  });

  const disabledReasonMessage = !isDisabled
    ? null
    : disabledReason === 'outOfStock'
    ? intl.formatMessage({ id: 'AddToCartButton.variantOutOfStock' })
    : intl.formatMessage({ id: 'AddToCartButton.selectOptionsFirst' });

  const labelId = isChooseOptions
    ? 'AddToCartButton.chooseOptions'
    : added
    ? 'AddToCartButton.added'
    : isDisabled && disabledReason === 'outOfStock'
    ? 'AddToCartButton.soldOut'
    : 'AddToCartButton.addToBasket';

  return (
    <>
      <button
        type="button"
        className={classes}
        onClick={handleClick}
        disabled={isDisabled}
        title={
          disabledReasonMessage ||
          (opensProductPage
            ? intl.formatMessage({ id: 'AddToCartButton.chooseOnProductPage' })
            : undefined)
        }
      >
        <FormattedMessage id={labelId} />
      </button>
      {showDisabledReason && disabledReasonMessage ? (
        <span className={css.disabledReason}>{disabledReasonMessage}</span>
      ) : null}
      {/* Screen readers hear the confirmation too, not only sighted shoppers. */}
      <span className={css.srOnly} role="status" aria-live="polite">
        {added ? intl.formatMessage({ id: 'AddToCartButton.addedAnnouncement' }) : ''}
      </span>
    </>
  );
};

AddToCartButtonComponent.propTypes = {
  className: string,
  rootClassName: string,
  listing: object.isRequired,
  quantity: number,
  variants: arrayOf(object),
  disabled: bool,
  disabledReason: oneOf(['incomplete', 'outOfStock']),
  onChooseOptions: func,
  opensProductPage: bool,
  showDisabledReason: bool,
  maxQuantity: number,
  variant: oneOf(['card', 'page']),
  onAddToCart: func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onAddToCart: (listing, quantity, variants, maxQuantity) =>
    dispatch(addToCart(listing, quantity, variants, maxQuantity)),
});

const AddToCartButton = compose(
  connect(
    null,
    mapDispatchToProps
  )
)(AddToCartButtonComponent);

export default AddToCartButton;
